import {
  handleResp,
  getDefaultHeaders,
  extendFetchOptions,
  handleCashifyBlobResp,
} from "./utils";
import type {
  WrappedCashifyFunction,
  GenericResponse,
  CommonResponse,
} from "./utils";
import {
  EkycIdentityType,
  EkycImageDTO,
  EkycRequestDetailResult,
  EkycRequestDTO,
  EkycReviewStatus,
  EkycStatus,
} from "./ekyc-service";
import {
  CaseTransactionRequestData,
  EkycTransactionEkycRequestResult,
} from "./ekyc-case-service";
import { CountryCode } from "libphonenumber-js";
import { StandardObject } from "utils";

export interface EkycCaseTransactionService {
  initializeEkycTransaction: ReturnType<typeof initializeEkycTransaction>;
  initializeIdvCheckTransaction: ReturnType<
    typeof initializeIdvCheckTransaction
  >;
  listCaseMemberTransaction: ReturnType<typeof listEkycCaseMemberTransaction>;
  listTransactionRequests: ReturnType<typeof listCaseTransactionRequests>;
  getCaseMemberTransactionDetail: ReturnType<
    typeof getEkycCaseMemberTransactionDetail
  >;
  deleteCaseMemberTransaction: ReturnType<
    typeof deleteEkycCaseMemberTransaction
  >;
  cancelCaseMemberTransaction: ReturnType<
    typeof cancelEkycCaseMemberTransaction
  >;
  getCaseTransactionRequestData: ReturnType<
    typeof getCaseTransactionRequestData
  >;
  getCaseTransactionUrl: ReturnType<typeof getCaseTransactionUrl>;
  getCaseRequestDetail: ReturnType<typeof getCaseRequestDetail>;
  getCaseRequestRawResult: ReturnType<typeof getCaseRequestRawResult>;
  getCaseRequestImages: ReturnType<typeof getCaseRequestImages>;
  generateCaseRequestReport: ReturnType<typeof generateCaseRequestReport>;
  putCaseRequestReviewStatus: ReturnType<typeof putCaseRequestReviewStatus>;
}

export type EkycCaseTransactionType = "EKYC" | "IDV_CHECK" | "NAME_CHECK";

export interface CreateEkycTransactionRequest {
  caseId: number;
  memberId: number;
  body: CreateEkycTransactionRequestBody;
}

interface CreateEkycTransactionRequestBody {
  provider: string;
  type: "EKYC";
  identityType: EkycIdentityType;
  identityNumber?: string;
}

interface CreateIdvCheckTransactionRequest {
  caseId: number;
  memberId: number;
  body: CreateIdvCheckTransactionRequestBody;
}

export interface CreateIdvCheckTransactionRequestBody {
  provider: string;
  type: "IDV_CHECK";
  firstName?: string;
  lastName?: string;
  completeName?: string;
  identityType?: EkycIdentityType;
  identityNumber?: string;
  dateOfBirth?: string;
  ekycRequestId?: number;
  useChineseNameInEykcRequest?: boolean;
  countryCode: CountryCode;
  addressLine1: string;
  city?: string;
  province?: string;
  postalCode?: string;
  countryCallingCode?: number; // trim leading +
  phoneNumber?: string;
  additionalData?: {
    userConsent: boolean;
    userAccountNumber?: string;
  };
}

export interface EkycCaseMemberTransactionDTO {
  id: number;
  type: EkycCaseTransactionType;
  verificationRequestData: CaseTransactionRequestData;
  latestEkycRequestId: number;
  latestStatus: EkycStatus;
  latestReviewStatus: EkycReviewStatus;
  cancelled: boolean;
  createTime: string;
  isTransactionUrlPathAvailable: boolean;
}

export interface TransactionRequestDTO
  extends Omit<EkycRequestDTO, "transactionType"> {
  ekycCaseMemberTransactionId: number;
  transactionType: EkycCaseTransactionType;
}

export interface EkycCaseMemberTransactionDetail
  extends EkycCaseMemberTransactionDTO {
  ekycRequests: EkycTransactionEkycRequestResult[];
}
export interface IdvCheckRawMessage {
  code: string;
  value: string;
}

type IdvCheckReliability = "10" | "20" | "30";

export interface IdvCheckRawResult {
  reliability: IdvCheckReliability;
  options: string;
  messages: IdvCheckRawMessage[];
}
export interface CaseRequestPutReviewStatusRequest {
  caseId: number;
  memberId: number;
  transactionId: number;
  ekycRequestId: number;
  detail: {
    reviewStatus?: EkycReviewStatus;
    reviewStatusReason?: string;
  };
}

export const listEkycCaseMemberTransaction: WrappedCashifyFunction<
  { caseId: number; memberId: number },
  GenericResponse<EkycCaseMemberTransactionDTO[]>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/transaction`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listCaseTransactionRequests: WrappedCashifyFunction<
  { caseId: number; memberId: number },
  GenericResponse<TransactionRequestDTO[]>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/ekyc-request`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const initializeEkycTransaction: WrappedCashifyFunction<
  CreateEkycTransactionRequest,
  GenericResponse<EkycCaseMemberTransactionDTO>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId, body }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/initialize/ekyc`;
    const postOpts = {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const initializeIdvCheckTransaction: WrappedCashifyFunction<
  CreateIdvCheckTransactionRequest,
  GenericResponse<EkycCaseMemberTransactionDTO>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId, body }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/initialize/identity-verification`;
    const postOpts = {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/json",
      },
    };
    let opts = extendFetchOptions(postOpts, options);
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getEkycCaseMemberTransactionDetail: WrappedCashifyFunction<
  { caseId: number; memberId: number; transactionId: number },
  GenericResponse<EkycCaseMemberTransactionDetail>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId, transactionId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/transaction/${transactionId}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteEkycCaseMemberTransaction: WrappedCashifyFunction<
  { caseId: number; memberId: number; transactionId: number },
  GenericResponse<EkycCaseMemberTransactionDetail>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId, transactionId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/transaction/${transactionId}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const cancelEkycCaseMemberTransaction: WrappedCashifyFunction<
  { caseId: number; memberId: number; transactionId: number },
  GenericResponse<EkycCaseMemberTransactionDetail>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId, transactionId }) => {
    let url = `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}/transaction/${transactionId}/cancel`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};


export const getCaseTransactionRequestData: WrappedCashifyFunction<
  { caseId: number; memberId: number; transactionId: number },
  GenericResponse<{ requestData: CaseTransactionRequestData }>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId, transactionId }) => {
    let url =
      `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}` +
      `/transaction/${transactionId}/request-data`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getCaseTransactionUrl: WrappedCashifyFunction<
  { caseId: number; memberId: number; transactionId: number },
  GenericResponse<{ transactionUrlPath: string }>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId, transactionId }) => {
    let url =
      `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}` +
      `/transaction/${transactionId}/transaction-url`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};


export const getCaseRequestDetail: WrappedCashifyFunction<
  {
    caseId: number;
    memberId: number;
    transactionId: number;
    ekycRequestId: number;
  },
  GenericResponse<EkycRequestDetailResult>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId, transactionId, ekycRequestId }) => {
    let url =
      `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}` +
      `/transaction/${transactionId}/ekyc-request/${ekycRequestId}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getCaseRequestRawResult: WrappedCashifyFunction<
  {
    caseId: number;
    memberId: number;
    transactionId: number;
    ekycRequestId: number;
  },
  GenericResponse<IdvCheckRawResult | StandardObject>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId, transactionId, ekycRequestId }) => {
    let url =
      `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}` +
      `/transaction/${transactionId}/ekyc-request/${ekycRequestId}/provider-raw-result`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getCaseRequestImages: WrappedCashifyFunction<
  {
    caseId: number;
    memberId: number;
    transactionId: number;
    ekycRequestId: number;
  },
  GenericResponse<EkycImageDTO[]>
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId, transactionId, ekycRequestId }) => {
    let url =
      `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}` +
      `/transaction/${transactionId}/ekyc-request/${ekycRequestId}/images`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const generateCaseRequestReport: WrappedCashifyFunction<
  {
    caseId: number;
    memberId: number;
    transactionId: number;
    ekycRequestId: number;
    signal?: AbortSignal;
  },
  { blob: Blob; name: string }
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId, transactionId, ekycRequestId, signal }) => {
    let url =
      `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}` +
      `/transaction/${transactionId}/ekyc-request/${ekycRequestId}/report`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: {
          Accept: "application/octet-stream",
        },
        signal,
      },
      options
    );
    return fetch(url, opts).then((resp) => handleCashifyBlobResp(resp));
  };
};

export const putCaseRequestReviewStatus: WrappedCashifyFunction<
  CaseRequestPutReviewStatusRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ caseId, memberId, transactionId, ekycRequestId, detail }) => {
    let url =
      `${urlPrefix}/api/v1/ekyc-case/${caseId}/member/${memberId}` +
      `/transaction/${transactionId}/ekyc-request/${ekycRequestId}/review`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
